import { CheckCircle, XCircle, Info, Lightbulb } from 'lucide-react'
import type { ContrastResultProps } from '../../types/contrast'
import { isLargeText, suggestAccessibleColor, getRequiredRatio } from '../../utils/contrastUtils'

export default function ContrastResult({
  evaluation,
  textConfig,
  elementType,
  foreground,
  background,
  onApplySuggestion
}: ContrastResultProps) {
  const largeText = isLargeText(textConfig.size, textConfig.weight)

  // Determine which result to highlight based on current settings
  const getRelevantResult = () => {
    if (elementType === 'ui-component') {
      return {
        aa: evaluation.wcagAA.uiComponents,
        label: 'UI Components',
        requiredAA: '3:1'
      }
    }

    if (largeText) {
      return {
        aa: evaluation.wcagAA.largeText,
        aaa: evaluation.wcagAAA.largeText,
        label: 'Large Text',
        requiredAA: '3:1',
        requiredAAA: '4.5:1'
      }
    }

    return {
      aa: evaluation.wcagAA.normalText,
      aaa: evaluation.wcagAAA.normalText,
      label: 'Normal Text',
      requiredAA: '4.5:1',
      requiredAAA: '7:1'
    }
  }

  const relevant = getRelevantResult()

  // Calculate color suggestion if AA fails
  const targetRatio = getRequiredRatio(textConfig.size, textConfig.weight, 'AA', elementType)
  const suggestion = !relevant.aa.passes && foreground.isValid && background.isValid
    ? suggestAccessibleColor(foreground.rgb, background.rgb, targetRatio)
    : null

  return (
    <div className="space-y-6">
      {/* Main Contrast Ratio Display */}
      <div className="text-center">
        <div className="text-sm text-gray-500 mb-1">Contrast Ratio</div>
        <div className="text-5xl font-bold text-gray-900">
          {evaluation.formattedRatio}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          Testing: <span className="font-medium">{relevant.label}</span>
        </div>
      </div>

      {/* Pass/Fail Summary for Current Settings */}
      <div className="grid grid-cols-2 gap-4">
        {/* WCAG AA Result */}
        <div className={`p-4 rounded-lg border-2 ${
          relevant.aa.passes
            ? 'border-green-200 bg-green-50'
            : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">WCAG AA</span>
            {relevant.aa.passes ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Pass
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <XCircle className="w-3 h-3 mr-1" />
                Fail
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600">
            Required: {relevant.requiredAA}
          </div>
        </div>

        {/* WCAG AAA Result (only for text) */}
        {elementType === 'text' && relevant.aaa && (
          <div className={`p-4 rounded-lg border-2 ${
            relevant.aaa.passes
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">WCAG AAA</span>
              {relevant.aaa.passes ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Pass
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <XCircle className="w-3 h-3 mr-1" />
                  Fail
                </span>
              )}
            </div>
            <div className="text-xs text-gray-600">
              Required: {relevant.requiredAAA}
            </div>
          </div>
        )}

        {/* Info for UI Components */}
        {elementType === 'ui-component' && (
          <div className="p-4 rounded-lg border-2 border-gray-200 bg-gray-50">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-600">
                WCAG AAA does not apply to UI components. Only a 3:1 ratio (AA) is required.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Color Suggestion */}
      {suggestion && onApplySuggestion && (
        <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
          <div className="flex items-start space-x-3">
            <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">
                Suggested Fix
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                Make your text color <span className="font-medium">{suggestion.direction}</span> to achieve a {suggestion.contrastRatio}:1 contrast ratio.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-700">Current:</span>
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: foreground.hex }}
                    title={foreground.hex}
                  />
                  <span className="text-xs font-mono text-amber-800">{foreground.hex}</span>
                </div>
                <span className="text-amber-600">→</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-700">Suggested:</span>
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{ backgroundColor: suggestion.color.hex }}
                    title={suggestion.color.hex}
                  />
                  <span className="text-xs font-mono text-amber-800">{suggestion.color.hex}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onApplySuggestion(suggestion.color)}
                  className="ml-auto px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Results Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700">Complete Results</h4>
        </div>
        <div className="divide-y divide-gray-200">
          {/* Normal Text Row */}
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-700">Normal Text</span>
            <div className="flex items-center space-x-3">
              <ResultBadge
                label="AA (4.5:1)"
                passes={evaluation.wcagAA.normalText.passes}
              />
              <ResultBadge
                label="AAA (7:1)"
                passes={evaluation.wcagAAA.normalText.passes}
              />
            </div>
          </div>

          {/* Large Text Row */}
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-700">Large Text</span>
            <div className="flex items-center space-x-3">
              <ResultBadge
                label="AA (3:1)"
                passes={evaluation.wcagAA.largeText.passes}
              />
              <ResultBadge
                label="AAA (4.5:1)"
                passes={evaluation.wcagAAA.largeText.passes}
              />
            </div>
          </div>

          {/* UI Components Row */}
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-700">UI Components</span>
            <div className="flex items-center space-x-3">
              <ResultBadge
                label="AA (3:1)"
                passes={evaluation.wcagAA.uiComponents.passes}
              />
              <span className="text-xs text-gray-400">N/A</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper component for result badges
function ResultBadge({ label, passes }: { label: string; passes: boolean }) {
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
      passes
        ? 'bg-green-100 text-green-700 border border-green-200'
        : 'bg-red-100 text-red-700 border border-red-200'
    }`}>
      {passes ? (
        <CheckCircle className="w-3 h-3 mr-1" />
      ) : (
        <XCircle className="w-3 h-3 mr-1" />
      )}
      {label}
    </span>
  )
}
